import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../services/api';
import {
  CheckCircle, XCircle, AlertTriangle, RefreshCw, Database, HardDrive,
  Archive, Table2, Activity, Gem, Users, ShoppingCart, Wrench,
  Shield, Wifi, Clock, Server, TrendingUp, Zap
} from 'lucide-react';

function StatusBadge({ status }) {
  if (status === 'ok') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}>
      <CheckCircle className="h-3 w-3" /> OK
    </span>
  );
  if (status === 'warning') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308', border: '1px solid rgba(234,179,8,0.3)' }}>
      <AlertTriangle className="h-3 w-3" /> Attention
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
      <XCircle className="h-3 w-3" /> Erreur
    </span>
  );
}

function CheckCard({ icon: Icon, title, check }) {
  if (!check) return null;
  const color = check.status === 'ok' ? '#22c55e' : check.status === 'warning' ? '#eab308' : '#ef4444';
  const bg = check.status === 'ok' ? 'rgba(34,197,94,0.06)' : check.status === 'warning' ? 'rgba(234,179,8,0.06)' : 'rgba(239,68,68,0.06)';
  return (
    <div className="rounded-xl p-4" style={{ background: bg, border: `1px solid ${color}33` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
            <Icon className="h-4 w-4" style={{ color }} />
          </div>
          <div>
            <div className="text-sm font-medium" style={{ color: '#e5e5e5' }}>{title}</div>
            <div className="text-xs mt-0.5" style={{ color: '#888' }}>{check.message}</div>
          </div>
        </div>
        <StatusBadge status={check.status} />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = '#d4af37' }) {
  return (
    <div className="rounded-xl p-4 text-center" style={{ background: '#1a1a24', border: '1px solid #2a2a3a' }}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2"
        style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div className="text-2xl font-bold mb-0.5" style={{ color }}>{value ?? '—'}</div>
      <div className="text-xs" style={{ color: '#666' }}>{label}</div>
    </div>
  );
}

export default function SystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/health/`);
      const d = await r.json();
      setData(d);
      setLastRefresh(new Date());
    } catch (e) {
      setData({ status: 'error', checks: { database: { status: 'error', message: 'Impossible de joindre le serveur: ' + e.message } }, stats: {} });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  useEffect(() => {
    if (!autoRefresh) return;
    const timer = setInterval(fetchHealth, 30000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchHealth]);

  const globalStatus = data?.status || 'loading';
  const globalColor = globalStatus === 'ok' ? '#22c55e' : globalStatus === 'warning' ? '#eab308' : '#ef4444';
  const globalBg = globalStatus === 'ok' ? 'rgba(34,197,94,0.08)' : globalStatus === 'warning' ? 'rgba(234,179,8,0.08)' : 'rgba(239,68,68,0.08)';

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0f' }}>
      <div className="max-w-5xl mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))', border: '1px solid rgba(212,175,55,0.3)' }}>
              <Shield className="h-6 w-6" style={{ color: '#d4af37' }} />
            </div>
            <div>
              <h1 className="text-xl font-semibold" style={{ color: '#e5e5e5' }}>Monitoring Système</h1>
              <p className="text-sm" style={{ color: '#666' }}>
                Surveillance en temps réel · Mise à jour automatique toutes les 30s
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <div className="text-xs flex items-center gap-1.5" style={{ color: '#555' }}>
                <Clock className="h-3 w-3" />
                {lastRefresh.toLocaleTimeString('fr-FR')}
              </div>
            )}
            <button onClick={() => setAutoRefresh(a => !a)}
              className="px-3 py-1.5 rounded-lg text-xs transition-all"
              style={{
                background: autoRefresh ? 'rgba(212,175,55,0.12)' : '#1a1a24',
                color: autoRefresh ? '#d4af37' : '#666',
                border: `1px solid ${autoRefresh ? 'rgba(212,175,55,0.3)' : '#2a2a3a'}`
              }}>
              <Zap className="h-3 w-3 inline mr-1" />
              {autoRefresh ? 'Auto ON' : 'Auto OFF'}
            </button>
            <button onClick={fetchHealth} disabled={loading}
              className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm transition-all"
              style={{ background: '#1a1a24', color: '#888', border: '1px solid #2a2a3a' }}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>

        {/* Global status banner */}
        {data && (
          <div className="rounded-2xl p-5 flex items-center gap-5"
            style={{ background: globalBg, border: `1px solid ${globalColor}33` }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: `${globalColor}15`, border: `2px solid ${globalColor}40` }}>
              {globalStatus === 'ok' ? (
                <CheckCircle className="h-8 w-8" style={{ color: globalColor }} />
              ) : globalStatus === 'warning' ? (
                <AlertTriangle className="h-8 w-8" style={{ color: globalColor }} />
              ) : (
                <XCircle className="h-8 w-8" style={{ color: globalColor }} />
              )}
            </div>
            <div className="flex-1">
              <div className="text-lg font-semibold" style={{ color: globalColor }}>
                {globalStatus === 'ok' ? 'Système opérationnel — Tout est vert ✓' :
                  globalStatus === 'warning' ? 'Système fonctionnel avec avertissements' :
                    'Anomalie détectée — Intervention requise'}
              </div>
              <div className="text-sm mt-1" style={{ color: '#888' }}>
                Temps de réponse : <strong style={{ color: '#bbb' }}>{data.response_time_ms}ms</strong>
                {data.timestamp && (
                  <span className="ml-4">
                    Dernier contrôle : {new Date(data.timestamp).toLocaleString('fr-FR')}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono px-3 py-1 rounded-lg"
                style={{ background: 'rgba(0,0,0,0.3)', color: '#666', border: '1px solid #222' }}>
                v{data.version || '1.0'} · Production
              </div>
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !data && (
          <div className="rounded-2xl p-8 text-center" style={{ background: '#111118', border: '1px solid #1e1e28' }}>
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-3" style={{ color: '#d4af37' }} />
            <p style={{ color: '#666' }}>Vérification du système en cours...</p>
          </div>
        )}

        {data && (
          <>
            {/* Checks grid */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#555' }}>
                Contrôles système
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CheckCard icon={Database} title="Base de données MySQL" check={data.checks?.database} />
                <CheckCard icon={Table2} title="Tables critiques (8)" check={data.checks?.tables} />
                <CheckCard icon={HardDrive} title="Espace disque" check={data.checks?.disk} />
                <CheckCard icon={Archive} title="Sauvegardes automatiques" check={data.checks?.backup} />
              </div>
            </div>

            {/* Stats */}
            {data.stats && !data.stats.error && (
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#555' }}>
                  Statistiques en direct
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard icon={Gem} label="Bijoux en stock" value={data.stats.bijoux_en_stock?.toLocaleString('fr-FR')} color="#d4af37" />
                  <StatCard icon={Users} label="Clients" value={data.stats.nb_clients?.toLocaleString('fr-FR')} color="#60a5fa" />
                  <StatCard icon={ShoppingCart} label="Ventes aujourd'hui" value={data.stats.ventes_aujourd_hui} color="#22c55e" />
                  <StatCard icon={Wrench} label="OFs actifs" value={data.stats.ofs_actifs} color="#a78bfa" />
                </div>
              </div>
            )}

            {/* Technical proof section */}
            <div className="rounded-2xl p-6" style={{ background: '#111118', border: '1px solid #1e1e28' }}>
              <div className="flex items-center gap-3 mb-4">
                <Server className="h-5 w-5" style={{ color: '#d4af37' }} />
                <h2 className="text-base font-semibold" style={{ color: '#e5e5e5' }}>Garanties techniques</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-xl p-4" style={{ background: '#0f0f14', border: '1px solid #1e1e28' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="h-4 w-4" style={{ color: '#22c55e' }} />
                    <span className="text-sm font-medium" style={{ color: '#ccc' }}>Redémarrage automatique</span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: '#666' }}>
                    Le serveur redémarre automatiquement en cas de panne via <strong style={{ color: '#888' }}>systemd</strong>.
                    Temps de récupération : &lt; 10 secondes.
                  </p>
                </div>
                <div className="rounded-xl p-4" style={{ background: '#0f0f14', border: '1px solid #1e1e28' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Archive className="h-4 w-4" style={{ color: '#60a5fa' }} />
                    <span className="text-sm font-medium" style={{ color: '#ccc' }}>Sauvegardes planifiées</span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: '#666' }}>
                    Sauvegarde automatique de la base de données toutes les heures.
                    Données jamais perdues, restauration possible à tout instant.
                  </p>
                </div>
                <div className="rounded-xl p-4" style={{ background: '#0f0f14', border: '1px solid #1e1e28' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Wifi className="h-4 w-4" style={{ color: '#a78bfa' }} />
                    <span className="text-sm font-medium" style={{ color: '#ccc' }}>Monitoring en temps réel</span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: '#666' }}>
                    Cette page vérifie l'état du système en direct. Visibilité totale sur
                    la base de données, le disque et les sauvegardes à chaque instant.
                  </p>
                </div>
              </div>
            </div>

            {/* Uptime / response time chart placeholder */}
            <div className="rounded-2xl p-5 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg, #111118, #0f0f14)', border: '1px solid #1e1e28' }}>
              <div className="flex items-center gap-4">
                <TrendingUp className="h-8 w-8" style={{ color: '#d4af37' }} />
                <div>
                  <div className="text-sm font-semibold" style={{ color: '#e5e5e5' }}>Infrastructure dédiée — VPS Haute performance</div>
                  <div className="text-xs mt-0.5" style={{ color: '#555' }}>
                    141 GB espace libre · MySQL + Flask + React · Certificat SSL · Accès sécurisé HTTPS
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: '#22c55e' }}>99.9%</div>
                <div className="text-xs" style={{ color: '#555' }}>disponibilité cible</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
